import dayjs from 'dayjs';
import { Disclosure, StatRow } from '@/components/UI';
import type { OptimizerJobRow } from './types';
import { formatCount, optimizerAnalysisSkippedColumns } from './helpers';

interface JobDiagnosticsProps {
    selectedJob: OptimizerJobRow;
    /** Auto-expanded while the job is running or failed; collapsed once completed. */
    defaultExpanded: boolean;
}

/** Pipeline internals (column materialization, funnel counts, worker health) tucked behind a disclosure. */
const JobDiagnostics = ({ selectedJob, defaultExpanded }: JobDiagnosticsProps) => {
    return (
        <Disclosure
            className="mb-4"
            defaultExpanded={defaultExpanded}
            key={`${selectedJob.id}-${defaultExpanded}`}
            title={<span className="text-sm text-zinc-300">Run diagnostics</span>}
            variant="plain"
        >
            <div className="grid grid-cols-1 gap-x-8 gap-y-1.5 pt-2 sm:grid-cols-2 xl:grid-cols-3">
                <StatRow
                    label="Feature columns materialized"
                    value={`${formatCount(selectedJob.materializationCompletedColumns)} / ${formatCount(selectedJob.materializationTotalColumns)}`}
                />
                <StatRow
                    label="Columns ready / excluded"
                    value={`${formatCount(selectedJob.materializationReadyColumns)} / ${formatCount(selectedJob.materializationExcludedColumns)}`}
                />
                <StatRow
                    label="Columns analyzed"
                    value={`${formatCount(selectedJob.analysisLoadedColumns)} / ${formatCount(selectedJob.analysisTotalColumns)}`}
                />
                <StatRow
                    label={<span title="Policy-suppressed plus no-value, high-missing, and low-variance columns">Columns skipped</span>}
                    value={formatCount(optimizerAnalysisSkippedColumns(selectedJob))}
                />
                <StatRow label="Combinations evaluated" value={selectedJob.combinationsEvaluated?.toLocaleString() ?? '-'} />
                <StatRow label="Candidates before CV" value={selectedJob.candidatesBeforeCV?.toLocaleString() ?? '-'} />
                <StatRow label="Candidates validated" value={selectedJob.candidatesAfterCV?.toLocaleString() ?? '-'} />
                <StatRow
                    label="Training dataset"
                    value={
                        selectedJob.optimizerDatasetId != null
                            ? `#${selectedJob.optimizerDatasetId} · ${(selectedJob.trainingDatasetRowCount ?? 0).toLocaleString()} rows · ` +
                              `${(selectedJob.trainingDatasetBoundaryPurgedRowCount ?? 0).toLocaleString()} purged · ` +
                              `${(selectedJob.trainingDatasetUnresolvedOutcomeRowCount ?? 0).toLocaleString()} unresolved`
                            : '-'
                    }
                />
                <StatRow
                    label="Walk-forward dataset"
                    value={
                        selectedJob.validationDatasetId != null
                            ? `#${selectedJob.validationDatasetId} · ${(selectedJob.validationDatasetRowCount ?? 0).toLocaleString()} rows · ` +
                              `${(selectedJob.validationDatasetBoundaryPurgedRowCount ?? 0).toLocaleString()} purged · ` +
                              `${(selectedJob.validationDatasetUnresolvedOutcomeRowCount ?? 0).toLocaleString()} unresolved`
                            : '-'
                    }
                />
                <StatRow
                    label="Last heartbeat"
                    value={selectedJob.heartbeatAt ? dayjs(selectedJob.heartbeatAt).format('MMM D h:mm A') : '-'}
                />
                <StatRow label="Worker recoveries" value={(selectedJob.workerRecoveryCount ?? 0).toLocaleString()} />
            </div>
        </Disclosure>
    );
};

export default JobDiagnostics;
